// ════════════════════════════════════════════════════════════
//  WillieAnnouncer.jsx — WILLIE, IN THE MIDDLE OF THE RING
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "definitely need Willie in the middle of the
//  ring doing the 'in this corner' type stuff :3"
//
//  THE BODY is Terror's Plaza Willie — `public/models/willie.glb`,
//  copied byte-identical from app-terror (md5 1f6a87af…, the 2026-09-23
//  Blender head, 39 joints). One being, one body (THE LAW OF ONE BODY):
//  never edit the glb here; if Terror's gets a new pass, copy it again.
//  THE POSE is ours — this file. The bone recipe (book arm at READ_POSE,
//  the index bone that folds into a fist) is the Pier's williePose
//  (app-unstable-pierre/src/willieModel3D.jsx), carried over, plus three
//  ring moves: TURN (to a corner), POINT (at it), HYPE (a hop, both feet).
//
//  The director (Ringside.jsx) writes `cue.current = { look, point, hype }`
//  every frame; Willie only eases toward it. No React state, no re-render.
//  If the glb fails to load, a plain stand-in keeps the ring from being
//  empty (the error is logged, never thrown at the player).
// ════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const WILLIE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/willie.glb`;
const MODEL_TOP = 1.4;               // the body's measured crown (see app-terror willieModel3D.jsx)
const NO_INK = /eyes|buckle|glyph|bookw|bodymoss|cuffs/;
const SELF_LIGHT = 0.4;
const GLYPH = 0x7ff0ff;              // the W on his pouch

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
let INK = null;
const inkMat = () => {
  if (INK) return INK;
  INK = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  INK.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n  transformed += normalize( normal ) * 0.0030;");
  };
  return INK;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  let glyph = null;
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    if (vc) o.material.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
    };
    o.frustumCulled = false;
    if (o.name === "wil_glyph") {
      glyph = o;
      o.material = new THREE.MeshBasicMaterial({ color: GLYPH, toneMapped: false });
      return;
    }
    if (NO_INK.test(o.name)) return;
    const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat()) : new THREE.Mesh(o.geometry, inkMat());
    if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
    h.name = o.name + "_ink"; h.frustumCulled = false;
    o.parent.add(h);
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B, glyph };
}

// ⚠ upperarmL / forearmL / handL MUST stay at the builder's READ_POSE —
// the book is authored in that pose and only sits in his hand while these
// three numbers agree. Every ring move uses the RIGHT arm only.
function announcerPose(B, t, pt, hype) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  // the hop: knees bend, he rises, lands
  const hop = Math.max(0, Math.sin(t * 7.5)) * hype;
  for (const [k, s] of [["L", 1], ["R", -1]]) {
    set("thigh" + k, -0.12 * hype + hop * -0.25, 0, s * 0.06);
    set("shin" + k, 0.22 * hype + hop * 0.35, 0, 0);
    set("foot" + k, -0.1 * hype, s * 0.05, 0);
  }
  const bob = Math.sin(t * 0.9) * 0.015;
  set("upperarmL", -0.42 + bob, 0.2, -0.1);
  set("forearmL", -1.05 - bob, 0.35, 0);
  set("handL", 0.15, -0.5, -0.25);
  // the free arm: at his side, or up and out at a corner
  const lift = Math.min(1, pt + hype * 0.6);
  set("upperarmR", -1.25 * lift + Math.sin(t * 7.5) * 0.12 * hype, 0.1 * lift, -0.08 + 0.32 * lift);
  set("forearmR", -0.15 * (1 - lift) - 0.25 * lift, 0, 0);
  set("handR", -0.2 * lift, 0, 0);
  set("indexR", -2.3 * (1 - pt), 0, 0);
  if (B.indexR) B.indexR.scale.setScalar(1 - 0.45 * (1 - pt));
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + hop * 0.06 - 0.02 * hype;
  set("spine", 0.03 - 0.08 * lift, 0, 0);
  set("chest", -0.02 + Math.sin(t * 1.1) * 0.012 - 0.06 * hype, 0, 0);
  set("neck", 0.06 - 0.1 * lift, 0, 0);
  // reading when idle; chin up to the crowd when he's calling
  set("head", 0.2 * (1 - lift) - 0.08 * lift + Math.sin(t * 0.6) * 0.02, Math.sin(t * 0.3) * 0.08 * (1 - lift), 0);
}

function WillieModel({ cue, h }) {
  const gltf = useLoader(GLTFLoader, WILLIE_GLB_URL);
  const { root, B, glyph } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const S = useRef({ yaw: 0, pt: 0, hype: 0 });
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05), t = clock.getElapsedTime(), st = S.current;
    const c = (cue && cue.current) || {};
    const want = c.look == null ? 0 : c.look;
    let d = want - st.yaw; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    st.yaw += d * Math.min(1, dt * 5);
    st.pt += ((c.point ? 1 : 0) - st.pt) * Math.min(1, dt * 7);
    st.hype += ((c.hype ? 1 : 0) - st.hype) * Math.min(1, dt * 6);
    o.rotation.y = st.yaw;
    announcerPose(B, t, st.pt, st.hype);
    if (glyph) glyph.material.color.setHex(GLYPH).multiplyScalar(0.85 + Math.sin(t * 2.4) * 0.15);
  });
  return <group ref={g}><primitive object={root} scale={h / MODEL_TOP} /></group>;
}

// the stand-in while the glb loads, or if it never does
function StandIn({ h }) {
  return (
    <group>
      <mesh position={[0, h * 0.42, 0]}><capsuleGeometry args={[h * 0.16, h * 0.5, 6, 12]} /><meshStandardMaterial color="#6b5a3a" /></mesh>
      <mesh position={[0, h * 0.86, 0]}><sphereGeometry args={[h * 0.15, 16, 12]} /><meshStandardMaterial color="#5f8a4a" /></mesh>
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[ringside] willie.glb did not load — the stand-in takes the ring", e); } // eslint-disable-line no-console
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export default function WillieAnnouncer({ cue, h = 1.9 }) {
  const fb = <StandIn h={h} />;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <WillieModel cue={cue} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}
